import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/viewbid/state/app-state';
import {
    AuctionDetail,
    AuctionStatus,
    GetFeaturedAuctions200ResponseAllOfDataInner,
    Reminder,
} from '@viewbid/ts-angular-client';
import { TranslateService } from '@ngx-translate/core';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import { AuctionCardState, AuctionSummary, CarouselLot } from '@viewbid/ui';
import { UpdateAuctionStatusAction } from 'src/app/viewbid/modules/auctions/state/auctions-page-state/auctions-page.actions';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { fromEvent, merge, Subject, Subscription, takeUntil } from 'rxjs';
import { TimerService } from '@/shared/services/timerService';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import {
    carouselLotId,
    getAuctionCardState,
    mapLotsToCarouselLots,
    mapToAuctionSummary,
} from '@/vb-utils/auction-ui-mapper.util';
import { AuctionLotGroupItem } from '@viewbid/ts-angular-client';

@Component({
    selector: 'vb-carousel-auction',
    templateUrl: './carousel-auction.component.html',
    styleUrls: ['./carousel-auction.component.scss'],
})
export class CarouselAuctionComponent implements OnInit, OnDestroy {
    @Input() auction!: GetFeaturedAuctions200ResponseAllOfDataInner;
    @Input() isGridView: boolean = false;

    summary!: AuctionSummary;
    carouselLots: CarouselLot[] = [];
    state: AuctionCardState = 'upcoming';
    isMobile: boolean = false;

    /** Reminder bell — only for signed-in users (reminders need an account). */
    showAlert: boolean = false;
    /** Known only after the dialog reports; we don't prefetch per card. */
    alertActive: boolean = false;
    showReminderDialog: boolean = false;

    private readonly destroy$: Subject<void> = new Subject<void>();
    private statusCheckSubscription: Subscription = new Subscription();

    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly store: Store<AppState>,
        private readonly socketService: SocketService,
        private readonly deviceCheckService: DeviceCheckService,
        private readonly timerService: TimerService,
        private readonly router: Router,
        private readonly biddingUtils: BiddingUtils,
        private readonly translate: TranslateService,
        private readonly serverClock: ServerClockService,
    ) {}

    ngOnInit(): void {
        this.rebuildView();

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
                this.cdr.detectChanges();
            });

        this.subscribeSocketResources();

        this.socketService.resource$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: string) => {
                this.handleSocketEvent(data);
            });

        this.timerService.notifyEndTimerCompleteForAuction(
            this.auction.toDatetime,
            this.auction.id,
        );
        this.timerService.notifyStartTimerCompleteForAuction(
            this.auction.fromDatetime,
            this.auction.id,
        );
        if (this.auction.firstLotCloseTime) {
            this.timerService.notifyClosingTimerCompleteForAuction(
                this.auction.firstLotCloseTime,
                this.auction.id,
            );
        }
        this.subscribeAuctionTimers();
        this.recomputeOnReturnToTab();
        this.serverClock.corrected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.rebuildView());

        // Never emits for anonymous visitors (the selector filters null users),
        // so the bell simply stays hidden when logged out.
        this.biddingUtils
            .getCurrentUserId()
            .pipe(takeUntil(this.destroy$))
            .subscribe((currentUserId: string) => {
                this.showAlert = !!currentUserId;
                this.cdr.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.statusCheckSubscription.unsubscribe();
    }

    onCardClicked(): void {
        this.router.navigate([`/auctions/${this.auction.id}`]);
    }

    onLotClicked(lot: CarouselLot): void {
        this.router.navigate([`/auctions/${this.auction.id}/${lot.id}`]);
    }

    get cardHref(): string {
        return `/auctions/${this.auction.id}`;
    }

    get alertLabel(): string {
        return this.translate.instant(
            this.alertActive
                ? 'AuctionsPage.manageAuctionReminders'
                : 'AuctionsPage.setAuctionReminder',
        );
    }

    get seeAllLabel(): string {
        return this.translate.instant('AuctionsPage.seeAllItems');
    }

    /** The trailing "See all items" tile only earns its place when the
     *  auction holds more lots than the carousel previews — numLots is the
     *  auction's full count on the featured-auctions row. */
    get showSeeAll(): boolean {
        return (
            (this.auction?.numLots ?? 0) >
            CarouselAuctionComponent.SEE_ALL_MIN_LOTS
        );
    }
    private static readonly SEE_ALL_MIN_LOTS: number = 10;

    /**
     * The scheduler dialog is typed to the detail payload but only reads
     * id / status / fromDatetime / toDatetime / firstLotCloseTime — all
     * carried by the featured-auctions row, so no extra fetch on open.
     */
    get auctionAsDetail(): AuctionDetail {
        return this.auction as unknown as AuctionDetail;
    }

    onAlertClicked(): void {
        this.showReminderDialog = true;
    }

    closeReminderDialog(): void {
        this.showReminderDialog = false;
        this.cdr.detectChanges();
    }

    onRemindersChange(reminders: Reminder[]): void {
        this.alertActive = reminders.length > 0;
        this.cdr.detectChanges();
    }

    get showCarousel(): boolean {
        return (
            !this.isGridView && !this.isMobile && this.carouselLots.length > 0
        );
    }

    /**
     * The closed-flip is driven by a 1s setInterval in TimerService, which
     * browsers throttle or suspend in backgrounded tabs — so a card can sit at
     * "closing" past toDatetime until a manual refresh. Re-derive the state
     * whenever the tab regains visibility/focus so a missed timer self-heals.
     * Skips once closed, since that's terminal.
     */
    private recomputeOnReturnToTab(): void {
        merge(
            fromEvent(document, 'visibilitychange'),
            fromEvent(window, 'focus'),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.state === 'closed' || document.hidden) return;
                this.rebuildView();
            });
    }

    private rebuildView(): void {
        const now: Date = new Date(this.serverClock.now());
        this.state = getAuctionCardState(this.auction, now);
        this.summary = mapToAuctionSummary(this.auction, now);
        this.carouselLots = mapLotsToCarouselLots(this.auction.lots);
        this.dispatchStatusForState();
        this.cdr.detectChanges();
    }

    private subscribeSocketResources(): void {
        const resources: { resourceId: string; listen: boolean }[] = [];
        if (this.auction?.id) {
            resources.push({
                resourceId: this.auction.id,
                listen: true,
            });
        }
        for (const lot of this.auction?.lots ?? []) {
            const resourceId: string | undefined = carouselLotId(lot);
            if (resourceId) {
                resources.push({ resourceId, listen: true });
            }
        }
        if (resources.length) {
            this.socketService.subscribeToResources(resources);
        }
    }

    private handleSocketEvent(data: string): void {
        let event: {
            action: string;
            data: {
                auction?: { id: string; endDate: string };
                auctionLotsId?: string;
                currentPrice?: number;
            } & Partial<GetFeaturedAuctions200ResponseAllOfDataInner>;
        };
        try {
            event = JSON.parse(data);
        } catch {
            return;
        }

        if (event.action === 'AUCTION_UPDATE') {
            if (event.data.id === this.auction.id) {
                const rest: typeof event.data = { ...event.data };
                delete rest.toDatetime;
                this.auction = { ...this.auction, ...rest };
                this.rebuildView();
                if (this.auction.firstLotCloseTime) {
                    this.timerService.notifyClosingTimerCompleteForAuction(
                        this.auction.firstLotCloseTime,
                        this.auction.id,
                    );
                }
            }
            return;
        }

        if (
            event.action === 'UPDATE_TIMES_EVENT' &&
            event.data.auction?.id === this.auction.id
        ) {
            this.auction = {
                ...this.auction,
                toDatetime: event.data.auction.endDate,
            };
            this.rebuildView();
            this.timerService.notifyEndTimerCompleteForAuction(
                this.auction.toDatetime,
                this.auction.id,
            );
            return;
        }

        if (
            (event.action === 'NEW_HIGH_BID' || event.action === 'PAGE_LOAD') &&
            event.data.auctionLotsId &&
            event.data.currentPrice !== undefined
        ) {
            this.applyBidUpdate(
                event.data.auctionLotsId,
                event.data.currentPrice,
            );
        }
    }

    private applyBidUpdate(auctionLotsId: string, currentBid: number): void {
        const idx: number = (this.auction.lots ?? []).findIndex(
            (lot: AuctionLotGroupItem) => carouselLotId(lot) === auctionLotsId,
        );
        if (idx === -1) return;
        const existing: any = this.auction.lots[idx];
        if (existing.currentBid === currentBid) return;
        this.auction = {
            ...this.auction,
            lots: this.auction.lots.map((lot: any, i: number) =>
                i === idx ? { ...lot, currentBid } : lot,
            ),
        };
        this.carouselLots = mapLotsToCarouselLots(this.auction.lots);
        this.cdr.detectChanges();
    }

    private dispatchStatusForState(): void {
        const statusByState: Record<AuctionCardState, AuctionStatus> = {
            upcoming: AuctionStatus.Public,
            open: AuctionStatus.Open,
            closing: AuctionStatus.Open,
            final: AuctionStatus.Open,
            closed: AuctionStatus.Complete,
        };
        this.store.dispatch(
            new UpdateAuctionStatusAction({
                auctionId: this.auction.id,
                status: statusByState[this.state],
            }),
        );
    }

    private subscribeAuctionTimers(): void {
        this.statusCheckSubscription.add(
            this.timerService.endTimerCompleteForAuction$.subscribe(
                (auctionsId: string) => {
                    if (this.auction && this.auction.id === auctionsId) {
                        this.rebuildView();
                    }
                },
            ),
        );
        this.statusCheckSubscription.add(
            this.timerService.startTimerCompleteForAuction$.subscribe(
                (auctionsId: string) => {
                    if (this.auction && this.auction.id === auctionsId) {
                        this.rebuildView();
                    }
                },
            ),
        );
        this.statusCheckSubscription.add(
            this.timerService.closingTimerCompleteForAuction$.subscribe(
                (auctionsId: string) => {
                    if (this.auction && this.auction.id === auctionsId) {
                        this.rebuildView();
                    }
                },
            ),
        );
        this.statusCheckSubscription.add(
            this.timerService.timerComplete$.subscribe(
                (auctionLotId: string) => {
                    const owns: boolean = (this.auction?.lots ?? []).some(
                        (lot: AuctionLotGroupItem) =>
                            carouselLotId(lot) === auctionLotId,
                    );
                    if (owns) this.rebuildView();
                },
            ),
        );
    }
}
